import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';

import { ModalsService } from '@ib-core/services/modals.service';
import { AbstractBaseComponent } from '@ib-shared/abstract/abstract-base';
import { helperBase64ToBytes } from '@ib-shared/helpers/base64-to-bytes';
import { helperCreateId } from '@ib-shared/helpers/create-id';
import { MediaDevicesService } from '@ib-shared/services/media-device.service';
import { WebcamImage } from 'ngx-webcam';
import { Observable, Subject, takeUntil } from 'rxjs';

import {
  ISelectedDocument,
  SELECTED_DOC_FORM_FIELDS,
  SELECTED_DOC_ID_PREFIX,
} from '../selected-docs/selected-docs.component';

@Component({
  selector: 'ib-webcam',
  templateUrl: './webcam.component.html',
})
export class IbWebcamComponent
  extends AbstractBaseComponent
  implements OnInit, OnDestroy
{
  @Input() btnClass: string;
  @Input() disabled: boolean;
  @Input() maxAmount = 1;
  public cameraIsVisible = false;
  public photos: Array<ISelectedDocument>;
  public showWebcam = false;
  public deviceId: string;
  public videoOptions: MediaTrackConstraints = {
    facingMode: {
      ideal: 'environment',
    },
  };
  public error: string;
  public cameras: Array<MediaDeviceInfo>;
  public selectedDeviceId: string;
  public webcamImage: WebcamImage = null;
  private trigger: Subject<void> = new Subject<void>();
  private nextWebcam: Subject<boolean | string> = new Subject<
    boolean | string
  >();
  public modalRef: MatDialogRef<HTMLElement>;
  public photosForm: FormGroup = new FormGroup({});
  public photosFormGroupsMap: Record<string, FormGroup> = {};

  get triggerObservable(): Observable<void> {
    return this.trigger.asObservable();
  }
  get nextWebcamObservable(): Observable<boolean | string> {
    return this.nextWebcam.asObservable();
  }
  get multipleWebcamsAvailable(): boolean {
    return Boolean(this.cameras.length > 1);
  }

  @Output() confirmed = new EventEmitter<Array<ISelectedDocument>>();

  @ViewChild('modalTmpl') modalTmpl: TemplateRef<HTMLElement>;

  constructor(
    private modalsS: ModalsService,
    private mediaDevicesS: MediaDevicesService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.initVideoDevices();
  }

  ngOnDestroy(): void {
    super.destroySubscriptions();
  }

  removePhoto(id: string): void {
    delete this.photosFormGroupsMap[id];
    this.photosForm.removeControl(id);
    this.photosForm.markAsTouched();
    this.photosForm.markAsDirty();
    this.photos = this.photos.filter(i => i.id !== id);
    this.photosForm.updateValueAndValidity();
  }

  deviceSelected(): void {
    this.showWebcam = true;
    this.nextWebcam.next(this.selectedDeviceId);
  }

  clear(): void {
    Object.keys(this.photosFormGroupsMap).forEach(key =>
      this.photosForm.removeControl(key),
    );
    this.photosFormGroupsMap = {};
    this.photos = [];
  }

  cancel(): void {
    this.clear();
    this.modalRef.close();
  }

  private initVideoDevices(): void {
    this.mediaDevicesS.selectVideoDevicesState().subscribe(data => {
      if (data.isInited) {
        if (data.error) {
          this.error = data.error;
        } else {
          const list = data.devices;
          if (list.length) {
            this.showWebcam = true;
            this.cameras = list;
            this.selectedDeviceId = this.cameras[0].deviceId;
            // delay for render webcam
            setTimeout(() => {
              this.nextWebcam.next(this.selectedDeviceId);
            });
          } else {
            this.error = 'No cameras available';
            this.showWebcam = false;
          }
        }
      }
    });
  }

  openModal(): void {
    this.modalRef = this.modalsS.openModal(this.modalTmpl, {
      maxWidth: '95vw',
      maxHeight: '95vh',
    });
    this.modalRef
      .beforeClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.clear();
      });
    this.mediaDevicesS.initVideoDevice().subscribe();
  }

  public triggerSnapshot(): void {
    this.trigger.next();
  }

  confirm(): void {
    const docs: Array<ISelectedDocument> = Object.keys(
      this.photosForm.controls,
    ).map(key => {
      const name = this.photosForm
        .get(key)
        .get(SELECTED_DOC_FORM_FIELDS.DOC_NAME).value;
      const doc = this.photosForm
        .get(key)
        .get(SELECTED_DOC_FORM_FIELDS.DOCUMENT).value;
      doc.name = name;
      return doc;
    });
    this.confirmed.emit(docs);
    this.photos = [];
    this.modalRef.close();
  }

  public showNextWebcam(directionOrDeviceId: boolean | string): void {
    this.cameraIsVisible = false;
    this.nextWebcam.next(directionOrDeviceId);
  }

  // todo - check this with photo !!!
  private base64toFile(base64: string, filename: string): File {
    if (base64.startsWith('/9j/')) {
      base64 = 'data:image/jpeg;base64,' + base64;
    }
    const arr = base64.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[arr.length - 1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename + '.jpeg', { type: mime });
  }

  public handleImage(webcamImage: WebcamImage): void {
    const name = helperCreateId('photo-');
    const file = this.base64toFile(webcamImage.imageAsBase64, `${name}`);
    const photo: ISelectedDocument = {
      name: name,
      id: helperCreateId(SELECTED_DOC_ID_PREFIX),
      webcamImage: webcamImage,
      createdAt: new Date(),
      file: file,
      sizeBytes: helperBase64ToBytes(webcamImage.imageAsBase64),
    };
    this.photos = [...(this.photos || []), photo];
    const fg = new FormGroup({
      [SELECTED_DOC_FORM_FIELDS.DOC_NAME]: new FormControl(photo.name, [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(400),
      ]),
      [SELECTED_DOC_FORM_FIELDS.DOCUMENT]: new FormControl(photo, [
        Validators.required,
      ]),
    });
    this.photosFormGroupsMap[photo.id] = fg;
    this.photosForm.addControl(photo.id, fg);
    this.photosForm.updateValueAndValidity();
    this.photosForm.markAsTouched();
    this.photosForm.markAsDirty();
    setTimeout(() => {
      document.getElementById(photo.id)?.focus();
    }, 50);
  }

  public cameraWasSwitched(deviceId: string): void {
    this.deviceId = deviceId;
    this.cameraIsVisible = true;
  }
}
